import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  writeBatch,
  addDoc,
  orderBy,
  limit as firestoreLimit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import type {
  User,
  ModerationAction,
  ModerationActionType,
  ModerationTargetType,
  ModerationFilter,
} from "../types";

export interface ReportedContent {
  id: string;
  type: "bar" | "drink" | "event" | "offer" | "user";
  entityId: string;
  entityName: string;
  barId?: string;
  barName?: string;
  reportedBy: string;
  reportedByName?: string;
  reason: string;
  reportedAt: Timestamp;
  status: "pending" | "resolved" | "dismissed";
  resolvedBy?: string;
  resolvedAt?: Timestamp;
  action?: string;
}

export interface ModerationStats {
  totalReports: number;
  pendingReports: number;
  resolvedReports: number;
  dismissedReports: number;
  bannedUsers: number;
}

const stripUndefinedDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .map(([key, entryValue]) => [key, stripUndefinedDeep(entryValue)]);

    return Object.fromEntries(entries);
  }

  return value;
};

/**
 * Lädt reported Content (simuliert - würde normalerweise eine reports Collection sein)
 * Für MVP: Sucht nach Entities mit reported=true Flag
 */
export const getReportedContent = async (): Promise<ReportedContent[]> => {
  try {
    // In production würde hier eine "reports" Collection abgefragt werden
    // Für MVP simulieren wir das mit einem leeren Array
    // eslint-disable-next-line no-console
    console.log(
      "getReportedContent called - returning empty array (no reports collection yet)"
    );
    return [];
  } catch (error) {
    console.error("Error fetching reported content:", error);
    throw error;
  }
};

/**
 * Resolved einen Report
 */
export const resolveReport = async (
  reportId: string,
  adminUid: string,
  action: string
): Promise<void> => {
  try {
    // In production würde hier der Report in der reports Collection updated werden
    // eslint-disable-next-line no-console
    console.log(
      `Resolving report ${reportId} by ${adminUid} with action: ${action}`
    );
  } catch (error) {
    console.error("Error resolving report:", error);
    throw error;
  }
};

/**
 * Dismissed einen Report
 */
export const dismissReport = async (
  reportId: string,
  adminUid: string
): Promise<void> => {
  try {
    // eslint-disable-next-line no-console
    console.log(`Dismissing report ${reportId} by ${adminUid}`);
  } catch (error) {
    console.error("Error dismissing report:", error);
    throw error;
  }
};

/**
 * Bannt einen User
 */
export const banUser = async (
  userId: string,
  adminUid: string,
  reason: string
): Promise<void> => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      isBlocked: true,
      blockedAt: Timestamp.now(),
      blockedBy: adminUid,
      blockReason: reason,
    });

    await createModerationAction("ban", "user", userId, adminUid, {
      reason,
    });
  } catch (error) {
    console.error("Error banning user:", error);
    throw error;
  }
};

/**
 * Entsperrt einen User
 */
export const unbanUser = async (
  userId: string,
  adminUid?: string
): Promise<void> => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      isBlocked: false,
      blockedAt: null,
      blockedBy: null,
      blockReason: null,
    });

    if (adminUid) {
      await createModerationAction("unban", "user", userId, adminUid);
    }
  } catch (error) {
    console.error("Error unbanning user:", error);
    throw error;
  }
};

/**
 * Löscht einen User-Datensatz (Auth-Account bleibt unberührt)
 */
export const deleteUserAccount = async (
  userId: string,
  adminUid: string,
  reason?: string
): Promise<void> => {
  try {
    await deleteDoc(doc(db, "users", userId));

    await createModerationAction("delete", "user", userId, adminUid, {
      targetName: userId,
      reason,
      notes: "User-Dokument in Firestore gelöscht (Auth-Konto nicht automatisch entfernt).",
    });
  } catch (error) {
    console.error("Error deleting user account:", error);
    throw error;
  }
};

/**
 * Lädt gebannte Users
 */
export const getBannedUsers = async (): Promise<User[]> => {
  try {
    const usersQuery = query(
      collection(db, "users"),
      where("isBlocked", "==", true)
    );
    const snapshot = await getDocs(usersQuery);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as User[];
  } catch (error) {
    console.error("Error fetching banned users:", error);
    return [];
  }
};

/**
 * Lädt Moderation Stats
 */
export const getModerationStats = async (): Promise<ModerationStats> => {
  try {
    const reportsSnapshot = await getDocs(collection(db, "contentReports"));
    const reports = reportsSnapshot.docs.map((d) => d.data() as { status?: string });
    const bannedUsers = await getBannedUsers();

    return {
      totalReports: reports.length,
      pendingReports: reports.filter((r) => r.status === "pending").length,
      resolvedReports: reports.filter((r) => r.status === "resolved").length,
      dismissedReports: reports.filter((r) => r.status === "dismissed").length,
      bannedUsers: bannedUsers.length,
    };
  } catch (error) {
    console.error("Error fetching moderation stats:", error);
    return {
      totalReports: 0,
      pendingReports: 0,
      resolvedReports: 0,
      dismissedReports: 0,
      bannedUsers: 0,
    };
  }
};

/**
 * Löscht Content (Bar, Drink, Event, Offer)
 */
export const deleteContent = async (
  type: "bar" | "drink" | "event" | "offer" | "story",
  entityId: string,
  barId?: string
): Promise<void> => {
  try {
    if (type === "bar") {
      await purgeBarContentAndResetBar(entityId);
    } else if (barId) {
      const batch = writeBatch(db);
      const collectionName = type === "story" ? "stories" : `${type}s`;
      const contentRef = doc(db, "bars", barId, collectionName, entityId);
      batch.delete(contentRef);
      await batch.commit();
    } else {
      throw new Error(`barId is required for ${type} deletion`);
    }
  } catch (error) {
    console.error("Error deleting content:", error);
    throw error;
  }
};

const deleteQueryInBatches = async (
  createQuery: () => ReturnType<typeof query>
): Promise<number> => {
  let deletedCount = 0;
  let hasMore = true;

  while (hasMore) {
    const snapshot = await getDocs(createQuery());
    if (snapshot.empty) {
      break;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => batch.delete(docSnap.ref));
    await batch.commit();
    deletedCount += snapshot.size;
    hasMore = snapshot.size >= 450;
  }

  return deletedCount;
};

const purgeBarContentAndResetBar = async (barId: string): Promise<void> => {
  const barRef = doc(db, "bars", barId);
  const barDoc = await getDoc(barRef);

  if (!barDoc.exists()) {
    throw new Error(`Bar ${barId} not found`);
  }

  const nestedCollections = ["drinks", "events", "offers", "stories"] as const;
  for (const nestedCollection of nestedCollections) {
    await deleteQueryInBatches(
      () => query(collection(db, "bars", barId, nestedCollection), firestoreLimit(450))
    );
  }

  await deleteQueryInBatches(
    () => query(collection(db, "claimRequests"), where("barId", "==", barId), firestoreLimit(450))
  );

  await deleteQueryInBatches(
    () => query(collection(db, "contentReports"), where("barId", "==", barId), firestoreLimit(450))
  );

  await deleteQueryInBatches(
    () => query(collection(db, "contentReports"), where("entityId", "==", barId), firestoreLimit(450))
  );

  await updateDoc(barRef, {
    contentPurged: true,
    contentPurgedAt: Timestamp.now(),
    isClaimed: false,
    ownerId: null,
    ownerName: null,
    ownerEmail: null,
    claimedAt: null,
    claimStatus: null,
    drinkCount: 0,
    eventCount: 0,
    offerCount: 0,
    storyCount: 0,
    adminBlocked: false,
    status: "active",
    visibility: "public",
    ownerManagedDescription: null,
    ownerManagedOpeningHours: null,
    updatedAt: Timestamp.now(),
  });
};

/**
 * Sperrt/entsperrt Inhalte oder Nutzer (Soft-Moderation).
 */
export const setEntityBlocked = async (
  type: ModerationTargetType,
  entityId: string,
  isBlocked: boolean,
  adminUid: string,
  barId?: string,
  reason?: string
): Promise<void> => {
  try {
    const now = Timestamp.now();

    if (type === "user") {
      if (isBlocked) {
        await banUser(entityId, adminUid, reason || "Moderation durch Admin");
      } else {
        await unbanUser(entityId, adminUid);
      }
      return;
    }

    const isBarLevel = type === "bar";
    const collectionName =
      type === "story" ? "stories" : isBarLevel ? "bars" : `${type}s`;

    const targetRef = isBarLevel
      ? doc(db, "bars", entityId)
      : doc(db, "bars", barId || "", collectionName, entityId);

    if (!isBarLevel && !barId) {
      throw new Error(`barId is required for ${type}`);
    }

    const baseUpdate: Record<string, unknown> = {
      isBlocked,
      blockedAt: isBlocked ? now : null,
      blockedBy: isBlocked ? adminUid : null,
      blockReason: isBlocked ? reason || null : null,
      updatedAt: now,
    };

    if (type === "drink") {
      baseUpdate.isAvailable = !isBlocked;
      baseUpdate.isHidden = isBlocked;
    }

    if (type === "event") {
      baseUpdate.isPublished = !isBlocked;
      baseUpdate.adminHidden = isBlocked;
    }

    if (type === "offer") {
      baseUpdate.isPublished = !isBlocked;
      baseUpdate.isActive = !isBlocked;
      baseUpdate.adminPaused = isBlocked;
    }

    if (type === "story") {
      baseUpdate.isActive = !isBlocked;
      baseUpdate.isExpired = isBlocked;
      baseUpdate.adminHidden = isBlocked;
    }

    if (type === "bar") {
      baseUpdate.adminBlocked = isBlocked;
      baseUpdate.visibility = isBlocked ? "unlisted" : "public";
    }

    await updateDoc(targetRef, baseUpdate);

    await createModerationAction(
      isBlocked ? "hide" : "unhide",
      type,
      entityId,
      adminUid,
      {
        barId,
        reason,
      }
    );
  } catch (error) {
    console.error("Error setting blocked state:", error);
    throw error;
  }
};

/**
 * Archiviert oder reaktiviert eine Bar inkl. Moderationslog.
 */
export const setBarArchiveState = async (
  barId: string,
  shouldArchive: boolean,
  adminUid: string,
  reason?: string
): Promise<void> => {
  try {
    const barRef = doc(db, "bars", barId);
    const now = Timestamp.now();
    const barSnapshot = await getDoc(barRef);

    if (!barSnapshot.exists()) {
      throw new Error(`Bar ${barId} not found`);
    }

    const barData = barSnapshot.data() as { name?: string };

    await updateDoc(barRef, {
      status: shouldArchive ? "archived" : "active",
      visibility: shouldArchive ? "unlisted" : "public",
      updatedAt: now,
      ...(shouldArchive
        ? {
            archivedAt: now,
            archivedBy: adminUid,
            archiveReason: reason || null,
            reactivatedAt: null,
            reactivatedBy: null,
          }
        : {
            reactivatedAt: now,
            reactivatedBy: adminUid,
            archiveReason: null,
          }),
    });

    await createModerationAction(
      shouldArchive ? "archive" : "reactivate",
      "bar",
      barId,
      adminUid,
      {
        targetName: barData.name || barId,
        reason,
      }
    );
  } catch (error) {
    console.error("Error updating archive state:", error);
    throw error;
  }
};

/**
 * Löscht gemeldeten Inhalt inkl. Audit-Log.
 */
export const deleteEntityWithLog = async (
  type: ModerationTargetType,
  entityId: string,
  adminUid: string,
  barId?: string,
  reason?: string
): Promise<void> => {
  try {
    if (type === "user") {
      await deleteUserAccount(entityId, adminUid, reason);
      return;
    }

    if (type === "bar" || type === "drink" || type === "event" || type === "offer" || type === "story") {
      await deleteContent(type, entityId, barId);
      await createModerationAction("delete", type, entityId, adminUid, {
        barId,
        reason,
      });
      return;
    }

    throw new Error(`Unsupported target type: ${type}`);
  } catch (error) {
    console.error("Error deleting entity with log:", error);
    throw error;
  }
};

/**
 * Erstellt einen Moderation Action Record
 */
export const createModerationAction = async (
  actionType: ModerationActionType,
  targetType: ModerationTargetType,
  targetId: string,
  adminId: string,
  options?: {
    targetName?: string;
    barId?: string;
    barName?: string;
    reason?: string;
    notes?: string;
  }
): Promise<string> => {
  try {
    const rawActionData: Omit<ModerationAction, "id"> = {
      actionType,
      targetType,
      targetId,
      adminId,
      ...(options?.targetName ? { targetName: options.targetName } : {}),
      ...(options?.barId ? { barId: options.barId } : {}),
      ...(options?.barName ? { barName: options.barName } : {}),
      ...(options?.reason ? { reason: options.reason } : {}),
      ...(options?.notes ? { notes: options.notes } : {}),
      isResolved: false,
      createdAt: Timestamp.now(),
    };

    const actionData = stripUndefinedDeep(
      rawActionData
    ) as Omit<ModerationAction, "id">;

    const docRef = await addDoc(
      collection(db, "moderationActions"),
      actionData
    );
    return docRef.id;
  } catch (error) {
    console.error("Error creating moderation action:", error);
    throw error;
  }
};

/**
 * Lädt Moderation Actions mit optionalen Filtern
 */
export const getModerationActions = async (
  filters?: ModerationFilter,
  limitCount = 50
): Promise<ModerationAction[]> => {
  try {
    let q = query(
      collection(db, "moderationActions"),
      orderBy("createdAt", "desc"),
      firestoreLimit(limitCount)
    );

    // Apply filters
    if (filters?.actionType) {
      q = query(q, where("actionType", "==", filters.actionType));
    }
    if (filters?.targetType) {
      q = query(q, where("targetType", "==", filters.targetType));
    }
    if (filters?.barId) {
      q = query(q, where("barId", "==", filters.barId));
    }
    if (filters?.adminId) {
      q = query(q, where("adminId", "==", filters.adminId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as ModerationAction[];
  } catch (error) {
    console.error("Error fetching moderation actions:", error);
    return [];
  }
};

/**
 * Lädt Moderation Actions für eine bestimmte Bar
 */
export const getModerationActionsByBar = async (
  barId: string
): Promise<ModerationAction[]> => {
  return getModerationActions({ barId });
};
